import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthProvider';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { motion } from 'framer-motion';
import {
  Mail, Lock, User, Eye, EyeOff, AlertCircle,
  Command, ArrowRight, Loader2,
  ShieldCheck
} from 'lucide-react';
import { FaGithub, FaGoogle } from 'react-icons/fa';
import { Icon } from '@/components/ui/icon';
import { Badge } from '@/components/ui/badge';
import AuthLeftPanel from './components/AuthLeftPanel';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!agree) {
      setError('You must agree to the Terms of Service & Privacy Policy');
      return;
    }

    setLoading(true);

    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password
    });

    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* ── Left panel — decorative ─────────────── */}
      <AuthLeftPanel titlePrefix="Begin Your Admin" titleSuffix="Journey Here." />

      {/* ── Right panel — registration form ─────────── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Badge className="rounded-xl p-2">
              <Icon icon={Command} color="white" />
            </Badge>
            <span className="font-black text-lg tracking-tight">shadex</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight">Create your account</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Get started with your free administrator profile today
            </p>
          </div>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-5 p-3 rounded-lg bg-danger/10 border border-danger/25 flex items-center gap-2.5"
            >
              <Icon icon={AlertCircle} color="danger" size="sm" className="shrink-0" />
              <p className="text-sm text-danger">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div className="space-y-1.5">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <Icon icon={User} className="absolute left-3 top-1/2 -translate-y-1/2" size="sm" color='muted' />
                <Input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="pl-9 h-10"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" >Email address</Label>
              <div className="relative">
                <Icon icon={Mail} size="sm" color="muted" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="pl-9 h-10"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password" >Password</Label>
              <div className="relative">
                <Icon icon={Lock} size="sm" color="muted" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-10"
                  autoComplete="new-password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <Icon icon={EyeOff} /> : <Icon icon={Eye} />}
                </Button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" >Confirm Password</Label>
              <div className="relative">
                <Icon icon={Lock} size="sm" color="muted" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-9 h-10"
                  required
                />
              </div>
            </div>

            {/* Agree to terms */}
            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="agree"
                checked={agree}
                onCheckedChange={(checked) => setAgree(checked === true)}
                className="mt-0.5"
                required
              />
              <Label htmlFor="agree" className="text-sm text-muted-foreground cursor-pointer leading-normal">
                I agree to the{' '}
                <a href="#" className="text-primary hover:underline underline-offset-4 font-semibold">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-primary hover:underline underline-offset-4 font-semibold">
                  Privacy Policy
                </a>
              </Label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 font-semibold gap-2"
            >
              {loading
                ? <><Icon icon={Loader2} size="sm" color="white" className=" animate-spin" /> Creating account…</>
                : <><span>Sign Up</span><Icon icon={ArrowRight} size="sm" color="white" /></>
              }
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">or continue with</span>
            <Separator className="flex-1" />
          </div>

          {/* Social buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="gap-2 text-sm" type="button">
              <Icon icon={FaGithub} size="sm" />
              GitHub
            </Button>
            <Button variant="outline" className="gap-2 text-sm" type="button">
              <Icon icon={FaGoogle} size="sm" />
              Google
            </Button>
          </div>

          {/* Login link */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-primary hover:underline underline-offset-4">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div >
  );
};

export default Register;